import { isoDate, resolveDates, stayRange } from '../dates';
import { type Engine, NeedsInputError } from '../types';
import { priceParam } from './shared';

const AIRBNB_AMENITIES: Array<
  [id: string, token: string, label: string, amenity: number]
> = [
  ['wifi', 'wifi', 'Wifi', 4],
  ['kitchen', 'kitchen', 'Kitchen', 8],
  ['parking', 'parking', 'Free parking', 9],
  ['ac', 'ac', 'Air conditioning', 5],
  ['washer', 'washer', 'Washer', 33],
  ['pool', 'pool', 'Pool', 7],
  ['hottub', 'hottub', 'Hot tub', 25],
  ['workspace', 'desk', 'Workspace', 47],
  ['fireplace', 'fireplace', 'Fireplace', 27],
  ['ev', 'ev', 'EV charger', 97],
];

const guests = {
  id: 'guests',
  type: 'number' as const,
  prefix: 'g',
  label: 'Adults',
  group: 'Dates',
  placeholder: '2',
};

export const airbnb: Engine = {
  id: 'airbnb',
  name: 'Airbnb',
  bang: 'air',
  aliases: ['airbnb', 'bnb'],
  homepage: 'https://www.airbnb.com',
  category: 'Travel',
  description: 'Homes and rooms. The query is the place: !air.wknd.g2 big sur.',
  params: [
    { id: 'dates', type: 'dates', label: 'Dates', group: 'Dates' },
    guests,
    {
      id: 'pets',
      type: 'flag',
      token: 'pets',
      label: 'Bringing pets',
      group: 'Dates',
    },
    { ...priceParam, help: 'Per night, in dollars.' },
    {
      id: 'room',
      type: 'choice',
      label: 'Type of place',
      group: 'Place',
      options: [
        { value: 'entire', token: 'entire', label: 'Entire home' },
        { value: 'private', token: 'room', label: 'Private room' },
        { value: 'hotel', token: 'hotel', label: 'Hotel' },
      ],
    },
    {
      id: 'property',
      when: { param: 'room', in: ['entire', 'private'], orUnset: true },
      whenLabel: 'doesn’t apply to hotels',
      type: 'choice',
      label: 'Property',
      group: 'Place',
      options: [
        { value: '1', token: 'house', label: 'House' },
        { value: '3', token: 'apt', label: 'Apartment' },
        { value: '2', token: 'guesthouse', label: 'Guesthouse' },
      ],
    },
    {
      id: 'bedrooms',
      when: { param: 'room', in: ['entire', 'private'], orUnset: true },
      whenLabel: 'doesn’t apply to hotels',
      type: 'number',
      prefix: 'b',
      label: 'Min bedrooms',
      group: 'Place',
      placeholder: '2',
    },
    ...AIRBNB_AMENITIES.map(([id, token, label]) => ({
      id,
      type: 'flag' as const,
      token,
      label,
      group: 'Amenities',
    })),
    {
      id: 'instant',
      type: 'flag',
      token: 'instant',
      label: 'Instant Book',
      group: 'Booking',
    },
    {
      id: 'flexCancel',
      type: 'flag',
      token: 'flexcancel',
      label: 'Free cancellation',
      group: 'Booking',
    },
    {
      id: 'favorite',
      type: 'flag',
      token: 'fav',
      label: 'Guest favorite',
      group: 'Booking',
    },
  ],
  build({ query, values }) {
    const url = new URL('https://www.airbnb.com/s/homes');
    if (query) url.searchParams.set('query', query);
    const stay =
      typeof values.dates === 'string' ? stayRange(values.dates) : undefined;
    if (stay) {
      url.searchParams.set('checkin', isoDate(stay.start));
      url.searchParams.set('checkout', isoDate(stay.end));
    }
    if (typeof values.guests === 'number')
      url.searchParams.set('adults', String(values.guests));
    if (values.pets === true) url.searchParams.set('pets', '1');
    if (typeof values.price === 'object') {
      if (values.price.min !== undefined)
        url.searchParams.set('price_min', String(values.price.min));
      if (values.price.max !== undefined)
        url.searchParams.set('price_max', String(values.price.max));
    }
    if (values.room === 'entire')
      url.searchParams.append('room_types[]', 'Entire home/apt');
    if (values.room === 'private')
      url.searchParams.append('room_types[]', 'Private room');
    if (values.room === 'hotel')
      url.searchParams.append('kg_and_tags[]', 'Tag:9613');
    if (typeof values.property === 'string')
      url.searchParams.append('l2_property_type_ids[]', values.property);
    if (typeof values.bedrooms === 'number')
      url.searchParams.set('min_bedrooms', String(values.bedrooms));
    for (const [id, , , amenity] of AIRBNB_AMENITIES)
      if (values[id] === true)
        url.searchParams.append('amenities[]', String(amenity));
    if (values.instant === true) url.searchParams.set('ib', 'true');
    if (values.flexCancel === true)
      url.searchParams.set('flexible_cancellation', 'true');
    if (values.favorite === true)
      url.searchParams.set('guest_favorite', 'true');
    return url.toString();
  },
};

export const booking: Engine = {
  id: 'booking',
  name: 'Booking.com',
  bang: 'bk',
  aliases: ['booking'],
  homepage: 'https://www.booking.com',
  category: 'Travel',
  description: 'Hotels and rentals. The query is the destination.',
  params: [
    { id: 'dates', type: 'dates', label: 'Dates', group: 'Dates' },
    guests,
    { ...priceParam, help: 'Per night, in US dollars.' },
    {
      id: 'review',
      type: 'choice',
      label: 'Review score',
      group: 'Filters',
      options: [
        { value: '80', token: '8plus', label: '8+ Very good' },
        { value: '90', token: '9plus', label: '9+ Wonderful' },
      ],
    },
    {
      id: 'stars',
      when: { param: 'type', in: ['204'], orUnset: true },
      whenLabel: 'doesn’t apply to apartments',
      type: 'choice',
      label: 'Stars',
      group: 'Filters',
      options: [
        { value: '3', token: '3star', label: '3 stars and up' },
        { value: '4', token: '4star', label: '4 stars and up' },
        { value: '5', token: '5star', label: '5 stars' },
      ],
    },
    {
      id: 'type',
      type: 'choice',
      label: 'Property',
      group: 'Filters',
      options: [
        { value: '204', token: 'hotel', label: 'Hotels' },
        { value: '201', token: 'apt', label: 'Apartments' },
      ],
    },
    {
      id: 'freeCancel',
      type: 'flag',
      token: 'freecancel',
      label: 'Free cancellation',
      group: 'Options',
    },
    {
      id: 'breakfast',
      type: 'flag',
      token: 'breakfast',
      label: 'Breakfast included',
      group: 'Options',
    },
    {
      id: 'pool',
      type: 'flag',
      token: 'pool',
      label: 'Pool',
      group: 'Options',
    },
    {
      id: 'pets',
      type: 'flag',
      token: 'pets',
      label: 'Pet friendly',
      group: 'Options',
    },
    {
      id: 'sort',
      type: 'choice',
      label: 'Sort',
      group: 'Sort',
      options: [
        { value: 'price', token: 'low', label: 'Price: low first' },
        {
          value: 'bayesian_review_score',
          token: 'rated',
          label: 'Top reviewed',
        },
        {
          value: 'class',
          token: 'stars',
          label: 'Stars: high first',
          when: { param: 'type', in: ['204'], orUnset: true },
          whenLabel: 'doesn’t apply to apartments',
        },
        { value: 'distance_from_search', token: 'closest', label: 'Distance' },
      ],
    },
  ],
  build({ query, values }) {
    const url = new URL('https://www.booking.com/searchresults.html');
    url.searchParams.set('ss', query);
    const stay =
      typeof values.dates === 'string' ? stayRange(values.dates) : undefined;
    if (stay) {
      url.searchParams.set('checkin', isoDate(stay.start));
      url.searchParams.set('checkout', isoDate(stay.end));
    }
    url.searchParams.set(
      'group_adults',
      String(typeof values.guests === 'number' ? values.guests : 2)
    );
    url.searchParams.set('no_rooms', '1');
    url.searchParams.set('group_children', '0');
    const nflt: string[] = [];
    if (typeof values.price === 'object')
      nflt.push(
        `price=USD-${values.price.min ?? 0}-${values.price.max ?? 'max'}-1`
      );
    if (typeof values.review === 'string')
      nflt.push(`review_score=${values.review}`);
    if (typeof values.stars === 'string')
      for (let n = Number(values.stars); n <= 5; n++) nflt.push(`class=${n}`);
    if (typeof values.type === 'string') nflt.push(`ht_id=${values.type}`);
    if (values.freeCancel === true) nflt.push('fc=2');
    if (values.breakfast === true) nflt.push('mealplan=1');
    if (values.pool === true) nflt.push('hotelfacility=433');
    if (values.pets === true) nflt.push('stay_type=1');
    if (nflt.length) url.searchParams.set('nflt', nflt.join(';'));
    if (typeof values.sort === 'string')
      url.searchParams.set('order', values.sort);
    return url.toString();
  },
};

const ROUTE = /^([a-z]{3})(?:\s+to\s+|[\s>-]+)([a-z]{3})$/i;

export const kayak: Engine = {
  id: 'kayak',
  name: 'Kayak flights',
  bang: 'fly',
  aliases: ['kayak', 'flights'],
  homepage: 'https://www.kayak.com/flights',
  category: 'Travel',
  description:
    'Flights by airport codes: !fly.11/20-11/24.nonstop sea bzn. One date means one-way.',
  example: { query: 'sea bzn', mods: ['11/20-11/24'] },
  params: [
    {
      id: 'dates',
      type: 'dates',
      label: 'Dates',
      group: 'Trip',
      help: 'One date for one-way, a range for round trip.',
    },
    {
      id: 'travelers',
      type: 'number',
      prefix: 'g',
      label: 'Adults',
      group: 'Trip',
      placeholder: '2',
    },
    {
      id: 'cabin',
      type: 'choice',
      label: 'Cabin',
      group: 'Trip',
      options: [
        { value: 'premium', token: 'premium', label: 'Premium economy' },
        { value: 'business', token: 'business', label: 'Business' },
        { value: 'first', token: 'first', label: 'First' },
      ],
    },
    {
      id: 'stops',
      type: 'choice',
      label: 'Stops',
      group: 'Filters',
      options: [
        { value: '0', token: 'nonstop', label: 'Nonstop' },
        { value: '0,1', token: '1stop', label: '1 stop or fewer' },
      ],
    },
    {
      id: 'sort',
      type: 'choice',
      label: 'Sort',
      group: 'Sort',
      options: [
        { value: 'price_a', token: 'low', label: 'Cheapest' },
        { value: 'duration_a', token: 'fast', label: 'Quickest' },
      ],
    },
  ],
  build({ query, values }) {
    const route = ROUTE.exec(query.trim());
    if (!route)
      throw new NeedsInputError(
        'Type two airport codes as the query, e.g. sea bzn or SEA-BZN.',
        'Kayak'
      );
    const range =
      typeof values.dates === 'string' ? resolveDates(values.dates) : undefined;
    if (!range)
      throw new NeedsInputError(
        'Flights need a date: add one like 11/20 or 11/20-11/24.',
        'Kayak'
      );
    const path = [
      `${route[1]?.toUpperCase()}-${route[2]?.toUpperCase()}`,
      isoDate(range.start),
      ...(range.single ? [] : [isoDate(range.end)]),
      ...(typeof values.cabin === 'string' ? [values.cabin] : []),
      ...(typeof values.travelers === 'number'
        ? [`${values.travelers}adults`]
        : []),
    ];
    const url = new URL(`https://www.kayak.com/flights/${path.join('/')}`);
    url.searchParams.set(
      'sort',
      (values.sort as string | undefined) ?? 'bestflight_a'
    );
    if (typeof values.stops === 'string')
      url.searchParams.set('fs', `stops=${values.stops}`);
    return url.toString();
  },
};
